import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Sparkles, Send, X, User } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUSES = ["new", "contacted", "qualified", "won", "lost"];

export const RequestDialog = ({ req, token, open, onOpenChange, onChanged }) => {
  const cfg = { headers: { Authorization: `Bearer ${token}` } };
  const [data, setData] = useState(req);
  const [reply, setReply] = useState("");
  const [clientMsg, setClientMsg] = useState("");
  const [notes, setNotes] = useState(req?.notes || "");
  const [drafting, setDrafting] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (open && req?.id) {
      axios.get(`${API}/admin/requests/${req.id}`, cfg).then((r) => { setData(r.data); setNotes(r.data.notes || ""); }).catch(() => {});
    }
  }, [open, req?.id]); // eslint-disable-line

  const refresh = () => axios.get(`${API}/admin/requests/${req.id}`, cfg).then((r) => { setData(r.data); onChanged?.(); }).catch(() => {});

  const draft = async () => {
    setDrafting(true);
    try {
      const { data: d } = await axios.post(`${API}/admin/requests/${req.id}/ai-reply`, {}, cfg);
      if (d.draft) setReply(d.draft); else toast.error("AI unavailable");
    } catch { toast.error("AI unavailable"); }
    finally { setDrafting(false); }
  };

  const send = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/admin/requests/${req.id}/messages`, { role: "admin", text: reply }, cfg);
      setReply(""); toast.success("Reply saved to thread"); refresh();
    } catch { toast.error("Failed"); } finally { setSending(false); }
  };

  const recordClient = async () => {
    if (!clientMsg.trim()) return;
    await axios.post(`${API}/admin/requests/${req.id}/messages`, { role: "client", text: clientMsg }, cfg);
    setClientMsg(""); refresh();
  };

  const setStatus = async (status) => { await axios.patch(`${API}/admin/requests/${req.id}`, { status }, cfg); refresh(); };
  const saveNotes = async () => { await axios.patch(`${API}/admin/requests/${req.id}`, { notes }, cfg); toast.success("Notes saved"); onChanged?.(); };

  if (!data) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="request-dialog">
        <DialogHeader><DialogTitle className="font-serif text-2xl">{data.name} · {data.request_type}</DialogTitle></DialogHeader>
        <div className="space-y-4 text-sm">
          <div className="rounded-lg bg-secondary/50 p-3 font-mono text-xs">
            <p>{data.email} · {data.phone || "—"} · prefers {data.preferred_contact || "—"}</p>
            <p className="mt-1 text-primary">{data.address || "—"} · {data.folio || "—"}</p>
          </div>
          <p className="whitespace-pre-wrap">{data.description}</p>

          {(data.images || []).length > 0 && (
            <div className="flex flex-wrap gap-2" data-testid="request-images">
              {data.images.map((p, i) => (
                <a key={i} href={`${API}/files/${p}?auth=${token}`} target="_blank" rel="noreferrer">
                  <img src={`${API}/files/${p}?auth=${token}`} alt="attachment" className="h-20 w-20 rounded-lg border border-border object-cover" />
                </a>
              ))}
            </div>
          )}

          <div className="flex items-center gap-2">
            <span className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground">Status</span>
            {STATUSES.map((s) => (
              <button key={s} data-testid={`status-${s}`} onClick={() => setStatus(s)}
                className={`rounded-full px-2.5 py-1 text-[11px] font-semibold capitalize ${data.status === s ? "bg-primary text-primary-foreground" : "bg-secondary text-foreground/70"}`}>{s}</button>
            ))}
          </div>

          {/* Conversation thread */}
          <div>
            <p className="mb-1 font-mono text-[10px] uppercase tracking-wide text-accent">Conversation</p>
            <div className="space-y-2" data-testid="request-thread">
              {(data.messages || []).map((m, i) => (
                <div key={i} className={`rounded-lg p-2.5 text-sm ${m.role === "admin" ? "bg-primary/10" : "bg-secondary/60"}`}>
                  <p className="font-mono text-[9px] uppercase tracking-wide text-muted-foreground">{m.role} · {m.at?.slice(0, 16).replace("T", " ")}</p>
                  <p className="mt-0.5 whitespace-pre-wrap">{m.text}</p>
                </div>
              ))}
              {(data.messages || []).length === 0 && <p className="text-xs text-muted-foreground">No messages yet.</p>}
            </div>
          </div>

          {/* AI reply */}
          <div className="rounded-lg border border-accent/40 bg-accent/5 p-3">
            <div className="mb-2 flex items-center justify-between">
              <p className="text-sm font-semibold">AI reply</p>
              <Button size="sm" variant="outline" className="gap-1.5" data-testid="ai-draft-btn" onClick={draft} disabled={drafting}>
                {drafting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} Generate with AI
              </Button>
            </div>
            <Textarea data-testid="ai-reply-text" value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Generate an AI reply or write your own…" className="min-h-[120px]" />
            <Button className="mt-2 gap-1.5" data-testid="send-reply-btn" onClick={send} disabled={sending || !reply.trim()}>
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send / Save reply
            </Button>
          </div>

          {/* Record client response */}
          <div className="flex gap-2">
            <Input data-testid="client-msg-input" value={clientMsg} onChange={(e) => setClientMsg(e.target.value)} placeholder="Record a client response (by phone/email)…" />
            <Button variant="outline" data-testid="record-client-btn" onClick={recordClient}>Record</Button>
          </div>

          {/* Notes */}
          <div>
            <p className="mb-1 font-mono text-[10px] uppercase tracking-wide text-muted-foreground">Internal notes</p>
            <Textarea data-testid="request-notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
            <Button size="sm" variant="outline" className="mt-2" data-testid="save-notes-btn" onClick={saveNotes}>Save notes</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const UserDialog = ({ userId, token, open, onOpenChange }) => {
  const cfg = { headers: { Authorization: `Bearer ${token}` } };
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && userId) {
      setLoading(true); setData(null);
      axios.post(`${API}/admin/users/${userId}/ai-summary`, {}, cfg)
        .then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
    }
  }, [open, userId]); // eslint-disable-line

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto" data-testid="user-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2 font-serif text-2xl"><User className="h-5 w-5" />{data?.user?.first_name || "Client"}</DialogTitle></DialogHeader>
        {loading && <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Generating AI summary…</div>}
        {data && (
          <div className="space-y-4 text-sm">
            <p className="font-mono text-xs text-muted-foreground">{data.user.email} · {data.user.phone || "—"}</p>
            {data.summary && (
              <div className="rounded-lg border border-accent/40 bg-accent/5 p-3" data-testid="user-ai-summary">
                <p className="mb-1 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wide text-accent"><Sparkles className="h-3.5 w-3.5" />AI summary</p>
                <p className="whitespace-pre-wrap">{data.summary}</p>
              </div>
            )}
            <div><p className="mb-1 font-mono text-[10px] uppercase tracking-wide text-muted-foreground">Properties ({data.properties.length})</p>
              {data.properties.map((p, i) => <p key={i} className="text-xs">{p.address} · {p.folio}</p>)}
            </div>
            <div><p className="mb-1 font-mono text-[10px] uppercase tracking-wide text-muted-foreground">Questions ({data.questions.length})</p>
              {data.questions.slice(0, 8).map((q, i) => <p key={i} className="text-xs text-muted-foreground">• {q.question}</p>)}
            </div>
            <div><p className="mb-1 font-mono text-[10px] uppercase tracking-wide text-muted-foreground">Requests ({data.requests.length})</p>
              {data.requests.map((r, i) => <p key={i} className="text-xs">• {r.request_type}: {(r.description || "").slice(0, 60)}</p>)}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
